import type { IndexAttributeItem } from "@/repo";
import { createMapboxFillAndOpacity, PLACE_LEVELS } from "@/constants";
import ControlPanelListSearch from "./ControlPanelListSearch";
import { useEffect, useMemo, useState, type RefObject } from "react";
import "./ControlPanelList.css";
import "./ControlPanelSelectedEntity.css";
import "./LocationPlaceAttributeList.css";
import type { Map } from "mapbox-gl";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { getPlaceAttributeDistribution } from "@/repo/attribute";
import AttributeDistributionChart from "../AttributeDistributionChart";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getEnvParam, type TileEnv } from "@/env";

interface LocationPlaceAttributeListProps {
    attributes: IndexAttributeItem[];
    map: RefObject<Map | undefined>;
    env: TileEnv;
}

function toSafeColumn(name: string): string {
    return name.replace(/[^A-Za-z0-9_]/g, "");
}

function applyLayer(map: Map, attributeName: string, levelValue: number) {
    if (map.getSource("mvt-tiles")) {
        if (map.getLayer("tiles")) map.removeLayer("tiles");
        if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
        map.removeSource("mvt-tiles");
    }

    const safeColumn = toSafeColumn(attributeName);

    map.addSource("mvt-tiles", {
        type: "vector",
        tiles: [
            `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/places/${levelValue}/${safeColumn}/{z}/{x}/{y}?${getEnvParam()}`,
        ],
    });

    map.addLayer({
        id: "tiles",
        type: "fill",
        source: "mvt-tiles",
        "source-layer": "places",
        paint: {
            ...createMapboxFillAndOpacity(["get", "value"]),
        },
    });

    map.addLayer({
        id: "tiles-values",
        type: "symbol",
        source: "mvt-tiles",
        "source-layer": "places",
        minzoom: 12,
        paint: {
            "text-color": "#fff",
            "text-halo-color": "#000",
            "text-halo-width": 1.5,
        },
        layout: {
            "text-field": [
                "format",
                ["get", "name"],
                { "font-scale": 1.0 },
                "\n",
                {},
                [
                    "number-format",
                    ["get", "value"],
                    { "max-fraction-digits": 2 },
                ],
                { "font-scale": 0.85 },
            ],
            "text-font": ["Source Code Pro", "Arial Unicode MS Bold"],
            "text-size": 11,
            "text-anchor": "center",
            "text-justify": "center",
        },
    });
}

interface SelectedPlaceAttributeProps {
    attribute: IndexAttributeItem;
    map: RefObject<Map | undefined>;
    level: number;
    setLevel(level: number): void;
    onClose(): void;
    env: TileEnv;
}

function SelectedPlaceAttribute({
    attribute,
    map,
    level,
    setLevel,
    onClose,
    env,
}: SelectedPlaceAttributeProps) {
    useEffect(() => {
        const m = map.current;
        if (!m) return;
        applyLayer(m, attribute.name, level);
        return () => {
            if (m.getLayer("tiles-values")) m.removeLayer("tiles-values");
            if (m.getLayer("tiles")) m.removeLayer("tiles");
            if (m.getSource("mvt-tiles")) m.removeSource("mvt-tiles");
        };
    }, [attribute.name, level, map, env]);

    const distributionQuery = useQuery({
        queryKey: ["place-attribute-distribution", level, attribute.name],
        queryFn: () =>
            getPlaceAttributeDistribution(level, toSafeColumn(attribute.name)),
    });

    return (
        <div className="selected-entity">
            <div className="selected-entity-title">
                <button
                    className="selected-entity-title__exit"
                    onClick={onClose}
                >
                    ←
                </button>
                <div className="selected-entity-title__main">
                    <h3 className="selected-entity-title__name">
                        {attribute.name}
                    </h3>
                    <span className="selected-entity-title__id">
                        ID: {attribute.id}
                    </span>
                </div>
            </div>
            <div className="selected-entity-content">
                <div className="place-level-select">
                    <label htmlFor="place-level">Level</label>
                    <select
                        id="place-level"
                        value={level}
                        onChange={(e) => setLevel(Number(e.target.value))}
                    >
                        {PLACE_LEVELS.map((l) => (
                            <option key={l.value} value={l.value}>
                                {l.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Distribution</h4>
                {distributionQuery.isPending ? (
                    <span>Loading distribution...</span>
                ) : distributionQuery.isError ? (
                    <span>Failed to load distribution</span>
                ) : (
                    <AttributeDistributionChart data={distributionQuery.data} />
                )}
            </div>
        </div>
    );
}

function LocationPlaceAttributeList(props: LocationPlaceAttributeListProps) {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");

    const selectedId = id ? Number(id) : undefined;
    const level = Number(
        searchParams.get("level") ?? PLACE_LEVELS[0]?.value ?? 2,
    );

    function setLevel(newLevel: number) {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("level", String(newLevel));
                return next;
            },
            { replace: true },
        );
    }

    const attributes = useMemo(() => {
        return props.attributes.filter((attribute) =>
            attribute.name
                .toLowerCase()
                .trim()
                .includes(search.toLowerCase().trim()),
        );
    }, [props.attributes, search]);

    const selectedAttribute = props.attributes.find((a) => a.id === selectedId);

    if (selectedAttribute) {
        return (
            <SelectedPlaceAttribute
                attribute={selectedAttribute}
                map={props.map}
                level={level}
                setLevel={setLevel}
                onClose={() => navigate("/location/attributes/place")}
                env={props.env}
            />
        );
    }

    return (
        <>
            <div className="place-level-select">
                <label htmlFor="place-level">Level</label>
                <select
                    id="place-level"
                    value={level}
                    onChange={(e) => setLevel(Number(e.target.value))}
                >
                    {PLACE_LEVELS.map((l) => (
                        <option key={l.value} value={l.value}>
                            {l.label}
                        </option>
                    ))}
                </select>
            </div>
            <ControlPanelListSearch
                type="attribute"
                search={search}
                setSearch={setSearch}
            />
            <div className="control-panel-button-list">
                {attributes.map((attribute) => (
                    <button
                        key={attribute.id}
                        onClick={() => {
                            setSearch("");
                            navigate(
                                `/location/attributes/place/${attribute.id}?level=${level}`,
                            );
                        }}
                        className={classNames(
                            "control-panel-button-list__item",
                            attribute.id === selectedId &&
                                "control-panel-button-list__item--active",
                        )}
                    >
                        <div></div>
                        {attribute.name}
                    </button>
                ))}
            </div>
        </>
    );
}

export default LocationPlaceAttributeList;
